import { useEffect, useRef } from 'react';

/**
 * Captured when this module first loaded — used as the local baseline for
 * the stale-deployment check against the server's BUILD_TIMESTAMP.
 */
const INITIAL_BUILD = Date.now();

/**
 * Module-level guard: once isats:update-available has been dispatched,
 * no subsequent source (SW updatefound, version poll) may dispatch again
 * until the next hard reload. Prevents double banners.
 */
let updateDispatched = false;
const dispatchOnce = () => {
  if (updateDispatched) return;
  updateDispatched = true;
  window.dispatchEvent(new CustomEvent('isats:update-available'));
};

/**
 * Handles service worker updates (silent) and stale-deployment detection.
 *
 * ## Service worker
 * Publishes a `'isats:update-available'` custom event on `window` when a
 * new SW installation is detected or the server BUILD_TIMESTAMP is newer
 * than our module load time. The App shell listens for this event to show
 * the update banner.
 *
 * The old silent auto-reload on `controllerchange` was removed because it
 * could lose unsaved user work.
 */
export default function PwaUpdater() {
  const checkedRef = useRef(false);
  const swHandlerRef = useRef<(() => void) | null>(null);

  // ── Service worker `updatefound` → dispatch custom event ──
  useEffect(() => {
    if (!('serviceWorker' in navigator)) return;

    const register = async () => {
      try {
        const reg = await navigator.serviceWorker.getRegistration();
        if (!reg) return;
        const onUpdateFound = () => { dispatchOnce(); };
        reg.addEventListener('updatefound', onUpdateFound);
        swHandlerRef.current = () => reg.removeEventListener('updatefound', onUpdateFound);
      } catch { /* ignore */ }
    };

    register();
    return () => { swHandlerRef.current?.(); };
  }, []);

  // ── Stale-deployment polling (fallback) ──
  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    const check = async () => {
      // Respect the 15s post-reload suppression window set by the
      // Refresh Now button.  Without this the banner can reappear
      // immediately after a reload if the poll fires before the
      // 15s window expires.
      const reloadedAt = sessionStorage.getItem('update_reload_at');
      if (reloadedAt) {
        const elapsed = Date.now() - parseInt(reloadedAt, 10);
        if (elapsed < 15_000) return;
        sessionStorage.removeItem('update_reload_at');
      }

      try {
        const fetchVersionWithRetry = async (retries = 3, backoff = 1000): Promise<any> => {
          try {
            const res = await fetch('/api/app/version');
            if (res.status === 503 && retries > 0) {
              await new Promise(r => setTimeout(r, backoff));
              return fetchVersionWithRetry(retries - 1, backoff * 2);
            }
            if (!res.ok) return null;
            return await res.json();
          } catch {
            if (retries > 0) {
              await new Promise(r => setTimeout(r, backoff));
              return fetchVersionWithRetry(retries - 1, backoff * 2);
            }
            return null;
          }
        };

        const data = await fetchVersionWithRetry();
        if (data?.build) {
          const serverTs = new Date(data.build).getTime();
          if (serverTs > INITIAL_BUILD) {
            dispatchOnce();
          }
        }
      } catch { /* offline */ }
    };

    check();
    const interval = setInterval(check, 5 * 60_000);
    return () => clearInterval(interval);
  }, []);

  return null;
}
